/** @format */

import { formatCurrency } from "../../utility/currencyUtils";
import { getCountryByCode } from "../../utility/stripeConnectCountries";

export const WEEK_DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

// Days can arrive as a string[] or as the JSON string CreateCircle submits.
export const normalizeDays = (days: unknown): string[] => {
    const parse = (value: unknown): unknown => {
        if (typeof value !== "string") return value;
        try {
            return JSON.parse(value);
        } catch {
            return value.split(",");
        }
    };

    const raw = parse(days);
    const list = Array.isArray(raw) ? raw.flatMap((d) => parse(d)) : [];

    return WEEK_DAYS.filter((day) =>
        list.some(
            (d) => typeof d === "string" && d.trim().toLowerCase() === day.toLowerCase(),
        ),
    );
};

export const formatContribution = (amount: unknown, currency?: unknown) => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return "—";
    const code = typeof currency === "string" && currency ? currency : "usd";
    try {
        return formatCurrency(value, code);
    } catch {
        // Unknown currency code: show the plain number so it isn't mislabelled as USD.
        return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
    }
};

export const getCurrencyCode = (currency?: unknown) =>
    (typeof currency === "string" && currency ? currency : "usd").toUpperCase();

export const getFrequency = (period?: unknown) => {
    const text = typeof period === "string" ? period : "";
    const key = text.toLowerCase().replace(/[^a-z]/g, "");
    if (key === "weekly") return { label: "Weekly", detail: "Every week" };
    if (key === "biweekly") return { label: "Bi-weekly", detail: "Every 2 weeks" };
    if (key === "monthly") return { label: "Monthly", detail: "Every month" };
    return { label: text || "Not set", detail: "" };
};

export const formatPayOutDay = (payOutDay?: unknown) => {
    const days = normalizeDays(payOutDay);
    if (days.length === 1) return `On ${days[0]}s`;
    if (days.length > 1) return days.map((d) => d.slice(0, 3)).join(", ");
    return typeof payOutDay === "string" && payOutDay.trim() ? payOutDay.trim() : "";
};

// Cycle dates are stored as UTC midnight of the calendar day, so format in UTC;
// local time would show the previous day for US timezones.
export const formatDate = (value?: unknown) => {
    if (!value) return "";
    const date = new Date(value as string);
    return Number.isNaN(date.getTime()) ?
            ""
        :   date.toLocaleDateString(undefined, { timeZone: "UTC" });
};

export const getCountryInfo = (code?: unknown) => {
    const upper = (typeof code === "string" ? code : "").trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(upper)) return null;
    const flag = upper.replace(/./g, (c) =>
        String.fromCodePoint(127397 + c.charCodeAt(0)),
    );
    return { code: upper, flag, name: getCountryByCode(upper)?.name ?? upper };
};

// Mirrors the server's getCircleCountry, which treats a missing country as "US".
export const getCircleCountryInfo = (code?: unknown) =>
    getCountryInfo(typeof code === "string" && code.trim() ? code : "US");

export const getSizeLabel = (membersCount?: string) => {
    if (membersCount === "<=5") return "Up to 5";
    if (membersCount === ">5 <10") return "6–9";
    if (membersCount === ">=10") return "10+";
    return "";
};
